import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { Download, Filter, Building2, Home, LogOut, Settings, X, RefreshCw, FileText, Sparkles, AlertTriangle, CheckCircle, Eye, ChevronDown, Printer, BarChart3, ClipboardList, Calendar, Users, History, Trash2, Smartphone, QrCode, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, LineChart, Line } from "recharts";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ empresa: "", setor: "", funcao: "" });
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiHistory, setAiHistory] = useState([]);
  const [showAiHistory, setShowAiHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [aiModel, setAiModel] = useState("openai/gpt-5.2");

  // Metadata for supported AI models (label + colors for badges)
  const AI_MODEL_OPTIONS = [
    { value: "openai/gpt-5.2", label: "GPT-5.2", short: "GPT-5.2", badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { value: "anthropic/claude-sonnet-5", label: "Claude Sonnet 5", short: "Claude Sonnet 5", badgeClass: "bg-amber-50 text-amber-700 border-amber-200" },
  ];
  const getModelMeta = (key) => AI_MODEL_OPTIONS.find(m => m.value === key) || AI_MODEL_OPTIONS[0];

  // Report emissions (timeline)
  const [emissionHistory, setEmissionHistory] = useState([]);
  const [loadingEmissions, setLoadingEmissions] = useState(false);

  // Public Share (presentation tokens)
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTokens, setShareTokens] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState("");
  const [shareDays, setShareDays] = useState(30);
  const [copiedTokenId, setCopiedTokenId] = useState(null);
  
  const aiSectionRef = useRef(null);
  const reportRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const statsRes = await axios.get(`${API}/assessments/stats`, { headers, params: filters });
      setStats(statsRes.data);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [filters, navigate]);

  const fetchAiHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const token = localStorage.getItem("admin_token");
      const params = filters.empresa ? { empresa: filters.empresa } : {};
      const response = await axios.get(`${API}/ai/analyses`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setAiHistory(response.data);
    } catch (error) {
      console.error("Erro ao buscar histórico de análises:", error);
    } finally {
      setLoadingHistory(false);
    }
  }, [filters.empresa]);

  const fetchEmissionHistory = useCallback(async () => {
    if (!filters.empresa) {
      setEmissionHistory([]);
      return;
    }
    try {
      setLoadingEmissions(true);
      const token = localStorage.getItem("admin_token");
      const params = { empresa: filters.empresa };
      if (filters.setor) params.setor = filters.setor;
      if (filters.funcao) params.funcao = filters.funcao;
      const response = await axios.get(`${API}/report-emissions`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setEmissionHistory(response.data || []);
    } catch (error) {
      console.error("Erro ao buscar histórico de emissões:", error);
    } finally {
      setLoadingEmissions(false);
    }
  }, [filters.empresa, filters.setor, filters.funcao]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchAiHistory();
  }, [fetchAiHistory]);

  useEffect(() => {
    fetchEmissionHistory();
  }, [fetchEmissionHistory]);

  const loadSavedAnalysis = (analysis) => {
    setAiAnalysis({
      interpretation: analysis.interpretation,
      control_suggestions: analysis.control_suggestions,
      priority_actions: analysis.priority_actions,
      general_recommendations: analysis.general_recommendations
    });
    setShowAiHistory(false);
    setTimeout(() => {
      aiSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const deleteAnalysis = async (analysisId, e) => {
    e.stopPropagation();
    if (!window.confirm("Tem certeza que deseja excluir esta análise?")) return;
    
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/ai/analyses/${analysisId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAiHistory();
    } catch (error) {
      console.error("Erro ao excluir análise:", error);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/assessments/export-csv`, {
        params: filters,
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "avaliacoes_nr01.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Erro ao exportar:", error);
    }
  };

  // Populate TOC page numbers before printing
  useEffect(() => {
    const populateToc = () => {
      // A4 portrait at 96dpi minus 10mm margins ≈ 1050 CSS pixels.
      const PAGE_HEIGHT_PX = 1050;
      try {
        const sections = Array.from(document.querySelectorAll("[data-toc-section]"));
        if (sections.length === 0) return;

        // Collect forced page-break boundaries (positions in document coordinates).
        // - .report-toc-page: page-break-AFTER (break occurs at element bottom)
        // - .report-cover-pagebreak: page-break-AFTER (break occurs at element bottom)
        // - .report-interactive-page: page-break-BEFORE (break occurs at element top)
        const breakPositions = [];
        document.querySelectorAll(".report-toc-page, .report-cover-pagebreak").forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.width === 0 && r.height === 0) return; // skip if invisible
          breakPositions.push(r.bottom + window.scrollY);
        });
        document.querySelectorAll(".report-interactive-page").forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.width === 0 && r.height === 0) return;
          breakPositions.push(r.top + window.scrollY);
        });
        breakPositions.sort((a, b) => a - b);

        sections.forEach((sec) => {
          const id = sec.getAttribute("data-toc-section");
          const ref = document.querySelector(`[data-toc-pageref="${id}"]`);
          if (!ref) return;
          const rect = sec.getBoundingClientRect();
          if (rect.width === 0 && rect.height === 0) return; // hidden -> leave placeholder
          const top = rect.top + window.scrollY;

          // Walk each segment defined by forced breaks. For each segment that ends
          // before this section, add its natural page count + 1 for the forced break.
          let pagesAdded = 0;
          let prevBoundary = 0;
          for (const bp of breakPositions) {
            if (bp <= top + 1) {
              const segmentLen = Math.max(0, bp - prevBoundary);
              pagesAdded += Math.floor(segmentLen / PAGE_HEIGHT_PX);
              pagesAdded += 1; // forced break itself
              prevBoundary = bp;
            } else {
              break;
            }
          }
          // Remaining natural pages in the current segment (after last forced break).
          const currentSegmentLen = Math.max(0, top - prevBoundary);
          pagesAdded += Math.floor(currentSegmentLen / PAGE_HEIGHT_PX);

          const page = 1 + pagesAdded;
          ref.textContent = String(page);
        });
      } catch (_) {
        // silent — TOC is non-critical
      }
    };
    window.addEventListener("beforeprint", populateToc);
    // Also populate once after layout (so it works for previews).
    const t = setTimeout(populateToc, 1500);
    return () => {
      window.removeEventListener("beforeprint", populateToc);
      clearTimeout(t);
    };
  }, [stats, filters.empresa, filters.setor, aiAnalysis]);

  const recordReportEmission = useCallback(async () => {
    if (!filters.empresa || !stats || (stats.total_assessments || 0) === 0) return;
    const POSITIVE_DIMS = ["Relações Sociais e Liderança", "Autonomia", "Sentido do Trabalho"];
    const dimensionScores = {};
    Object.entries(stats.dimension_averages || {}).forEach(([name, value]) => {
      const v = Number(value) || 0;
      dimensionScores[name] = POSITIVE_DIMS.includes(name) ? +(6 - v).toFixed(2) : +v.toFixed(2);
    });
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(
        `${API}/report-emissions`,
        {
          empresa: filters.empresa,
          setor: filters.setor || null,
          funcao: filters.funcao || null,
          total_assessments: stats.total_assessments || 0,
          overall_score: Number(stats.overall_score) || 0,
          dimension_scores: dimensionScores,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchEmissionHistory();
    } catch (e) {
      console.error("Erro ao registrar emissão:", e);
    }
  }, [filters.empresa, filters.setor, filters.funcao, stats, fetchEmissionHistory]);

  const handlePrintReport = () => {
    recordReportEmission();
    window.print();
  };

  const handleOpenPresentation = () => {
    if (!filters.empresa) return;
    const params = new URLSearchParams({ empresa: filters.empresa });
    if (filters.setor) params.append("setor", filters.setor);
    if (filters.funcao) params.append("funcao", filters.funcao);
    window.open(`/presentation?${params.toString()}`, "_blank", "noopener,noreferrer");
  };

  const fetchShareTokens = useCallback(async () => {
    if (!filters.empresa) return;
    try {
      setShareLoading(true);
      const token = localStorage.getItem("admin_token");
      const qs = new URLSearchParams({ empresa: filters.empresa });
      if (filters.setor) qs.append("setor", filters.setor);
      if (filters.funcao) qs.append("funcao", filters.funcao);
      const res = await axios.get(`${API}/presentation/tokens?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShareTokens(res.data || []);
    } catch (err) {
      setShareError(err.response?.data?.detail || "Erro ao carregar links");
    } finally {
      setShareLoading(false);
    }
  }, [filters.empresa, filters.setor, filters.funcao]);

  const openShareModal = async () => {
    if (!filters.empresa) return;
    setShareError("");
    setShowShareModal(true);
    await fetchShareTokens();
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setShareError("");
    setCopiedTokenId(null);
  };

  const handleCreateShareToken = async () => {
    if (!filters.empresa) return;
    setShareLoading(true);
    setShareError("");
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(
        `${API}/presentation/tokens`,
        {
          empresa: filters.empresa,
          setor: filters.setor || null,
          funcao: filters.funcao || null,
          days: shareDays,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchShareTokens();
    } catch (err) {
      setShareError(err.response?.data?.detail || "Erro ao criar link");
    } finally {
      setShareLoading(false);
    }
  };

  const handleRevokeShareToken = async (id) => {
    if (!window.confirm("Revogar este link público? Quem o tiver salvo não conseguirá mais acessá-lo.")) return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/presentation/tokens/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchShareTokens();
    } catch (err) {
      setShareError(err.response?.data?.detail || "Erro ao revogar link");
    }
  };

  const handleCopyShareLink = async (id, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedTokenId(id);
      setTimeout(() => setCopiedTokenId(null), 2000);
    } catch (_) {
      // fallback: keep silent
    }
  };

  const buildPublicUrl = (tok) => `${window.location.origin}/p/${tok.token}`;

  // Auto-fetch share tokens when filters change (used by the print QR Code)
  useEffect(() => {
    if (filters.empresa) {
      fetchShareTokens();
    } else {
      setShareTokens([]);
    }
  }, [filters.empresa, filters.setor, filters.funcao, fetchShareTokens]);

  // Pick the best matching active token for the current scope (exact match preferred)
  const activeShareToken = useMemo(() => {
    if (!shareTokens || shareTokens.length === 0) return null;
    const wantSetor = filters.setor || null;
    const wantFuncao = filters.funcao || null;
    const exact = shareTokens.find(
      (t) => (t.setor || null) === wantSetor && (t.funcao || null) === wantFuncao
    );
    return exact || null;
  }, [shareTokens, filters.setor, filters.funcao]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError("As senhas não coincidem");
      return;
    }

    if (passwordData.new_password.length < 6) {
      setPasswordError("A nova senha deve ter no minimo 6 caracteres");
      return;
    }

    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPasswordSuccess("Senha alterada com sucesso!");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
      
      setTimeout(() => {
        setShowSettings(false);
        setPasswordSuccess("");
      }, 2000);
    } catch (error) {
      if (error.response?.status === 401) {
        setPasswordError("Senha atual incorreta");
      } else {
        setPasswordError("Erro ao alterar senha. Tente novamente.");
      }
    }
  };

  const handleAIAnalysis = async () => {
    if (!stats || Object.keys(stats.dimension_averages).length === 0) {
      setAiError("Não há dados suficientes para análise");
      return;
    }

    setLoadingAI(true);
    setAiError("");

    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/ai/analyze`, {
        dimension_averages: stats.dimension_averages,
        total_assessments: stats.total_assessments,
        empresa: filters.empresa || "Consolidado Geral",
        setor: filters.setor || "Multisetorial",
        funcao: filters.funcao || "Operacional Consolidado",
        ai_model: aiModel
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAiAnalysis(response.data);
      
      // Refresh history after generating new analysis
      fetchAiHistory();
      
      setTimeout(() => {
        aiSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error("Erro na análise de IA:", error);
      setAiError(error.response?.data?.detail || "Erro ao gerar análise. Tente novamente.");
    } finally {
      setLoadingAI(false);
    }
  };

  const resetFilters = () => {
    setFilters({ empresa: "", setor: "", funcao: "" });
  };

  // Dimensões POSITIVAS (pontuação alta = situação favorável, NÃO é risco)
  // Nestas dimensões, a escala de risco é INVERTIDA
  const POSITIVE_DIMENSIONS = [
    "Relações Sociais e Liderança",
    "Autonomia", 
    "Sentido do Trabalho"
  ];

  // Dimensões NEGATIVAS (pontuação alta = risco)
  // Nestas dimensões, pontuação alta indica problema
  const NEGATIVE_DIMENSIONS = [
    "Exigências do Trabalho",
    "Insegurança e Conflitos"
  ];

  const isPositiveDimension = (dimensionName) => {
    return POSITIVE_DIMENSIONS.includes(dimensionName);
  };

  const getRiskLevel = (average, dimensionName = null) => {
    // Para dimensões positivas, invertemos a interpretação
    // Pontuação alta = situação favorável (baixo risco)
    // Pontuação baixa = situação desfavorável (alto risco)
    let effectiveAverage = average;

    if (dimensionName && isPositiveDimension(dimensionName)) {
      // Inverter: 5 vira 1, 4 vira 2, 3 continua 3, 2 vira 4, 1 vira 5
      effectiveAverage = 6 - average;
    }

    // Faixas oficiais (exibidas na Seção 4. Fundamentação da Classificação):
    //   BAIXO:    1,0 – 2,0
    //   MODERADO: 2,1 – 3,0
    //   ALTO:     3,1 – 4,0
    //   CRÍTICO:  4,1 – 5,0
    // Para valores intermediários (ex.: 3.02), arredondamos a 1 casa decimal
    // antes de classificar para que a faixa numérica seja honrada literalmente.
    const score = Math.round((Number(effectiveAverage) + Number.EPSILON) * 10) / 10;

    if (score >= 1.0 && score <= 2.0) {
      return { level: "Baixo", color: "#10B981", bgColor: "bg-green-500", textColor: "text-green-700" };
    } else if (score >= 2.1 && score <= 3.0) {
      return { level: "Moderado", color: "#CA8A04", bgColor: "bg-yellow-600", textColor: "text-yellow-800" };
    } else if (score >= 3.1 && score <= 4.0) {
      return { level: "Alto", color: "#F97316", bgColor: "bg-orange-500", textColor: "text-orange-700" };
    } else if (score >= 4.1 && score <= 5.0) {
      return { level: "Crítico", color: "#EF4444", bgColor: "bg-red-500", textColor: "text-red-700" };
    }
    return { level: "Sem dados", color: "#9CA3AF", bgColor: "bg-gray-500", textColor: "text-gray-700" };
  };

  const getRiskExposure = (average, dimensionName = null) => {
    let effectiveAverage = average;
    
    if (dimensionName && isPositiveDimension(dimensionName)) {
      effectiveAverage = 6 - average;
    }
    
    return Math.round(((effectiveAverage - 1) / 4) * 100);
  };

  const getBarColor = (value) => {
    if (value <= 1) return "#FEF3C7"; // Very light yellow
    if (value <= 2) return "#FDE68A"; // Light yellow
    if (value <= 3) return "#FCD34D"; // Yellow
    if (value <= 4) return "#FBBF24"; // Orange-yellow
    return "#F97316"; // Orange
  };

  const chartData = stats
    ? Object.entries(stats.dimension_averages).map(([name, value]) => {
        const avgValue = parseFloat(value.toFixed(2));
        const riskInfo = getRiskLevel(avgValue, name);
        const exposure = getRiskExposure(avgValue, name);
        return { name, value: avgValue, color: riskInfo.color, riskInfo, exposure, isPositive: isPositiveDimension(name) };
      })
    : [];

  // Calculate risk distribution based on effective risk levels
  const gravityDistribution = chartData.length > 0 ? [
    { name: "Baixo", value: chartData.filter(d => d.riskInfo.level === "Baixo").length, color: "#10B981" },
    { name: "Moderado", value: chartData.filter(d => d.riskInfo.level === "Moderado").length, color: "#CA8A04" },
    { name: "Alto", value: chartData.filter(d => d.riskInfo.level === "Alto").length, color: "#F97316" },
    { name: "Crítico", value: chartData.filter(d => d.riskInfo.level === "Crítico").length, color: "#EF4444" }
  ].filter(d => d.value > 0) : [];

  // Separate metrics for control vs intervention based on effective risk level
  const controlMetrics = chartData.filter(d => 
    ["Baixo", "Moderado"].includes(d.riskInfo.level)
  );
  const interventionMetrics = chartData.filter(d => 
    ["Alto", "Crítico"].includes(d.riskInfo.level)
  );

  // Get distribution data for a dimension
  const getDistributionData = (dimensionName) => {
    if (!stats?.dimension_distributions?.[dimensionName]) {
      return [
        { level: "1", value: 0, color: "#10B981", label: "Baixo" },
        { level: "2", value: 0, color: "#10B981", label: "Baixo" },
        { level: "3", value: 0, color: "#CA8A04", label: "Moderado" },
        { level: "4", value: 0, color: "#F97316", label: "Alto" },
        { level: "5", value: 0, color: "#EF4444", label: "Crítico" }
      ];
    }

    const dist = stats.dimension_distributions[dimensionName];
    const isPositive = isPositiveDimension(dimensionName);
    
    if (isPositive) {
      // Para dimensões positivas, invertemos a ordem E as cores:
      // Respostas altas (4-5) = situação favorável = baixo risco (verde)
      // Respostas baixas (1-2) = situação desfavorável = alto risco (laranja/vermelho)
      return [
        { level: "5", value: dist["5"] || 0, color: "#10B981", riskLabel: "Baixo" },
        { level: "4", value: dist["4"] || 0, color: "#10B981", riskLabel: "Baixo" },
        { level: "3", value: dist["3"] || 0, color: "#CA8A04", riskLabel: "Moderado" },
        { level: "2", value: dist["2"] || 0, color: "#F97316", riskLabel: "Alto" },
        { level: "1", value: dist["1"] || 0, color: "#EF4444", riskLabel: "Crítico" }
      ];
    }
    
    // Para dimensões negativas, escala normal
    return [
      { level: "1", value: dist["1"] || 0, color: "#10B981", riskLabel: "Baixo" },
      { level: "2", value: dist["2"] || 0, color: "#10B981", riskLabel: "Baixo" },
      { level: "3", value: dist["3"] || 0, color: "#CA8A04", riskLabel: "Moderado" },
      { level: "4", value: dist["4"] || 0, color: "#F97316", riskLabel: "Alto" },
      { level: "5", value: dist["5"] || 0, color: "#EF4444", riskLabel: "Crítico" }
    ];
  };

  // CSS-only Pie Chart for Print (using conic-gradient)
  const PrintPieChart = ({ data }) => {
    const total = data.reduce((acc, item) => acc + item.value, 0);
    if (total === 0) return null;
    
    let gradientParts = [];
    let currentAngle = 0;
    
    data.forEach((item) => {
      const percentage = (item.value / total) * 100;
      const nextAngle = currentAngle + (percentage * 3.6);
      gradientParts.push(`${item.color} ${currentAngle}deg ${nextAngle}deg`);
      currentAngle = nextAngle;
    });
    
    const gradient = `conic-gradient(${gradientParts.join(', ')})`;
    
    return (
      <div className="flex flex-col items-center">
        <div 
          style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: gradient,
            position: 'relative'
          }}
        >
          <div 
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              backgroundColor: 'white'
            }}
          />
        </div>
        <div className="flex flex-wrap justify-center gap-2 mt-2">
          {data.map((item, idx) => (
            <div key={idx} className="flex items-center gap-1 text-[8px]">
              <div style={{ width: '8px', height: '8px', backgroundColor: item.color, borderRadius: '2px' }}></div>
              <span>{Math.round((item.value / total) * 100)}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // CSS-only Bar Chart for Print
  const PrintBarChart = ({ data }) => {
    const maxValue = Math.max(...data.map(d => d.value), 1);
    
    return (
      <div className="flex items-end justify-between gap-1" style={{ height: '45px' }}>
        {data.map((item, idx) => (
          <div key={idx} className="flex flex-col items-center flex-1">
            <div 
              style={{
                width: '100%',
                maxWidth: '25px',
                height: `${Math.max((item.value / maxValue) * 35, 2)}px`,
                backgroundColor: item.color,
                borderRadius: '2px 2px 0 0',
                minHeight: item.value > 0 ? '4px' : '2px'
              }}
            />
            <span className="text-[7px] text-gray-500 mt-0.5">{item.level}</span>
          </div>
        ))}
      </div>
    );
  };

  // Dimension Card Component
  const DimensionCard = ({ dimension, isIntervention = false }) => {
    const riskInfo = getRiskLevel(dimension.value, dimension.name);
    const exposure = getRiskExposure(dimension.value, dimension.name);
    const distributionData = getDistributionData(dimension.name);
    const isPositive = isPositiveDimension(dimension.name);
    
    // Calcular a pontuação efetiva de risco
    const effectiveRiskScore = isPositive ? (6 - dimension.value).toFixed(2) : dimension.value;

    return (
      <div className="bg-white rounded-xl p-5 print:p-2 border border-gray-200 shadow-sm print:shadow-none">
        <div className="flex justify-between items-start mb-3 print:mb-1">
          <div>
            <h4 className="font-bold text-gray-800 uppercase text-sm print:text-xs">{dimension.name}</h4>
            {isPositive && (
              <span className="text-[10px] text-indigo-500 font-medium">Dimensão Favorável</span>
            )}
          </div>
          <span className={`px-3 py-1 print:px-2 print:py-0.5 ${riskInfo.bgColor} text-white text-xs print:text-[8px] font-bold rounded`}>
            {riskInfo.level.toUpperCase()}
          </span>
        </div>
        
        <p className="text-xs print:text-[8px] text-gray-500 mb-2 print:mb-1">
          {exposure}% de exposição ao risco
        </p>
        
        {/* Progress Bar */}
        <div className="w-full h-2 print:h-1 bg-gray-200 rounded-full overflow-hidden mb-4 print:mb-2">
          <div 
            className="h-full transition-all duration-500"
            style={{ width: `${exposure}%`, backgroundColor: riskInfo.color }}
          />
        </div>

        {/* Distribution Chart */}
        <div className="mt-4 print:mt-1">
          <div className="flex items-center gap-1 text-xs print:text-[7px] text-gray-500 mb-2 print:mb-1">
            <BarChart3 className="w-3 h-3 print:w-2 print:h-2" />
            <span className="uppercase tracking-wide">Distribuição de Risco (%)</span>
          </div>
          
          {/* Recharts - Screen only */}
          <div style={{ width: '100%', height: '80px' }} className="print:hidden">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distributionData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                <XAxis 
                  dataKey="level" 
                  tick={{ fontSize: 9 }} 
                  axisLine={false} 
                  tickLine={false} 
                />
                <YAxis hide />
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Respostas']}
                  labelFormatter={(label) => {
                    const item = distributionData.find(d => d.level === label);
                    return item?.riskLabel ? `${item.riskLabel} (nível ${label})` : `Nível ${label}`;
                  }}
                />
                <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                  {distributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* CSS Bar Chart - Print only */}
          <div className="hidden print:block">
            <PrintBarChart data={distributionData} />
          </div>
          
          <div className="flex justify-between text-xs print:text-[7px] text-gray-400 mt-1">
            <span>BAIXO</span>
            <span>MODERADO</span>
            <span>CRÍTICO</span>
          </div>
        </div>

        {/* Average Score */}
        <div className="mt-3 pt-3 print:mt-1 print:pt-1 border-t border-gray-100">
          <div className="flex justify-between items-center">
            <span className="text-xs print:text-[8px] text-gray-500">Pontuação de Risco:</span>
            <span className="text-lg print:text-sm font-bold" style={{ color: riskInfo.color }}>{effectiveRiskScore}</span>
          </div>
          {isPositive && (
            <p className="text-[9px] text-gray-400 mt-1">
              Média original: {dimension.value} (invertida para risco)
            </p>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Carregando dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white">
      {/* Header - Hide on print */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 print:hidden">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard Administrativo</h1>
            <p className="text-sm text-gray-500">Análise de Riscos Psicossociais NR-01</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => navigate("/")} data-testid="home-button" className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all text-sm">
              <Home className="w-4 h-4" />
              Inicio
            </button>
            <button onClick={() => navigate("/companies")} data-testid="companies-button" className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all text-sm">
              <Building2 className="w-4 h-4" />
              Empresas
            </button>
            <button onClick={() => setShowSettings(true)} data-testid="settings-button" className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all text-sm">
              <Settings className="w-4 h-4" />
            </button>
            <button onClick={handleLogout} data-testid="logout-button" className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-all text-sm">
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </div>
        </div>
      </header>

      {/* Print Header */}
      <div className="hidden print:block p-4 border-b border-gray-200">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900">Relatório de Riscos Psicossociais - NR-01</h1>
            <p className="text-sm text-gray-600 mt-1">Empresa: {filters.empresa || "Consolidado Geral"} | Setor: {filters.setor || "Todos"} | Função: {filters.funcao || "Todas"}</p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            {/* Client logo — only on Multisetorial print (empresa selecionada SEM setor) */}
            {filters.empresa && !filters.setor && stats?.company_info?.logo_base64 && (
              <img
                src={stats.company_info.logo_base64}
                alt={`Logo ${filters.empresa}`}
                className="h-20 w-auto max-w-[160px] object-contain"
                data-testid="print-header-client-logo"
              />
            )}
            <img
              src="/viaseg-logo-print.jpeg"
              alt="ViaSeg - Assessoria em Medicina Ocupacional"
              className="h-20 w-auto object-contain"
              style={{ mixBlendMode: 'multiply' }}
            />
          </div>
        </div>
      </div>

      {/* Painel de Filtragem - SEMPRE no topo, fora do reportRef para não ir para a impressão */}
      <div className="max-w-7xl mx-auto px-6 pt-6 print:hidden" data-testid="filter-panel-wrapper">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-2">
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 flex-1">
              Painel de Filtragem e Auditoria
            </h2>
            <button
              onClick={() => navigate("/companies")}
              data-testid="cadastro-empresa-btn"
              className="flex items-center gap-2 px-3 py-1.5 text-orange-600 hover:bg-orange-50 rounded-md transition-all text-xs font-semibold uppercase tracking-wide border border-orange-200"
            >
              <Building2 className="w-3.5 h-3.5" />
              Cadastro de Empresa
            </button>
          </div>
          <p className="text-xs text-gray-700 leading-relaxed text-justify mb-3">
            Selecione o recorte da análise (empresa, setor e função) para visualizar os
            indicadores consolidados. As alterações refletem dinamicamente em todas as
            seções do relatório.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div className="border border-gray-300 rounded-md p-2">
              <label className="block text-[9px] uppercase tracking-wide text-gray-500 font-semibold mb-1">Empresa / Unidade</label>
              <div className="relative">
                <select
                  data-testid="filter-empresa"
                  value={filters.empresa}
                  onChange={(e) => setFilters({ empresa: e.target.value, setor: "", funcao: "" })}
                  className="w-full h-9 px-2 pr-8 rounded border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all outline-none bg-white appearance-none text-sm text-gray-800 font-semibold"
                >
                  <option value="">Todas ({stats?.companies.length || 0})</option>
                  {stats?.companies.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            <div className="border border-gray-300 rounded-md p-2">
              <label className="block text-[9px] uppercase tracking-wide text-gray-500 font-semibold mb-1">Setor / Departamento</label>
              <div className="relative">
                <select
                  data-testid="filter-setor"
                  value={filters.setor}
                  onChange={(e) => setFilters({ ...filters, setor: e.target.value, funcao: "" })}
                  className="w-full h-9 px-2 pr-8 rounded border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all outline-none bg-white appearance-none text-sm text-gray-800 font-semibold"
                >
                  <option value="">Todos ({stats?.sectors.length || 0})</option>
                  {stats?.sectors.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            <div className="border border-gray-300 rounded-md p-2">
              <label className="block text-[9px] uppercase tracking-wide text-gray-500 font-semibold mb-1">Função Ocupacional</label>
              <div className="relative">
                <select
                  data-testid="filter-funcao"
                  value={filters.funcao}
                  onChange={(e) => setFilters({ ...filters, funcao: e.target.value })}
                  className="w-full h-9 px-2 pr-8 rounded border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all outline-none bg-white appearance-none text-sm text-gray-800 font-semibold"
                >
                  <option value="">Todas ({stats?.functions.length || 0})</option>
                  {stats?.functions.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
              <span>Base consolidada: <strong className="text-gray-800">{stats?.total_assessments || 0}</strong> registros localizados</span>
            </div>
            <button
              onClick={resetFilters}
              data-testid="reset-filters-btn"
              className="text-orange-600 hover:text-orange-700 text-xs font-semibold uppercase tracking-wide"
            >
              Resetar Filtros
            </button>
          </div>
        </div>

        {/* Action Toolbar — logo após o Painel de Filtragem (apenas com empresa selecionada) */}
        {filters.empresa && stats && stats.total_assessments > 0 && (
          <div className="flex flex-wrap gap-3 justify-end mt-3" data-testid="report-actions-toolbar">
            <button
              onClick={handlePrintReport}
              data-testid="print-report-btn"
              className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all font-semibold text-sm"
            >
              <Printer className="w-4 h-4" />
              IMPRIMIR RELATÓRIO
            </button>
            <button
              onClick={handleOpenPresentation}
              data-testid="open-presentation-btn"
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all font-semibold text-sm"
              title="Abrir apresentação em nova aba"
            >
              <Sparkles className="w-4 h-4" />
              GERAR APRESENTAÇÃO
            </button>
            <button
              onClick={openShareModal}
              data-testid="open-share-modal-btn"
              className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-purple-500 text-purple-700 rounded-lg hover:bg-purple-50 transition-all font-semibold text-sm relative"
              title="Gerar/gerenciar link público da apresentação"
            >
              <Smartphone className="w-4 h-4" />
              LINK PÚBLICO
              {activeShareToken && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full ring-2 ring-white" title="Link público ativo"></span>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Print-only Cover / Intro Section — appears ONLY for Multisetorial (empresa selecionada SEM setor específico) */}
      {filters.empresa && !filters.setor && (
        <div className="px-6 py-4 print:px-6 print:py-4 text-gray-800 report-cover bg-white rounded-xl border border-gray-200 shadow-sm mt-4 mx-4 print:mt-0 print:mx-0 print:border-0 print:rounded-none print:shadow-none" data-testid="report-cover-section">
          {/* Sumário do Documento — primeira página interna do laudo, antes da Identificação (PRINT-ONLY) */}
          <section className="hidden print:block report-toc-page break-inside-avoid mb-4" data-testid="report-toc-section">
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-4">
              Sumário do Documento
            </h2>
            <p className="text-[10px] text-gray-500 italic mb-3">
              Índice de navegação. As páginas indicadas são aproximadas e podem variar
              levemente conforme as configurações de impressão do navegador.
            </p>
            <ol className="report-toc-list space-y-1.5 text-xs text-gray-800 list-none p-0">
              {[
                { n: 1, title: "Identificação da Empresa" },
                { n: 2, title: "Objetivo do Diagnóstico" },
                { n: 3, title: "Metodologia Aplicada — COPSOQ II (Versão Curta)" },
                { n: 4, title: "Fundamentação da Classificação de Risco" },
                { n: 5, title: "Dimensões Avaliadas" },
                { n: 6, title: "Sumário Executivo" },
                { n: 7, title: "Resumo Executivo" },
                { n: 8, title: "Análise Quantitativa de Riscos" },
                { n: 9, title: "Escala de Gravidade Global" },
                ...(stats?.sector_comparison && stats.sector_comparison.length > 1
                  ? [{ n: 10, title: "Comparativo entre Setores" }]
                  : []),
                {
                  n: stats?.sector_comparison && stats.sector_comparison.length > 1 ? 11 : 10,
                  title: "Métricas Detalhadas por Nível",
                },
                {
                  n: stats?.sector_comparison && stats.sector_comparison.length > 1 ? 12 : 11,
                  title: "Panorama Geral (Radar)",
                },
                {
                  n: stats?.sector_comparison && stats.sector_comparison.length > 1 ? 13 : 12,
                  title: "Análise de Riscos — Interpretação e Conclusão",
                },
                {
                  n: 14,
                  title: "Versão Interativa — Apresentação Executiva (QR Code)",
                  refSection: 14,
                },
              ].map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-baseline gap-2 py-0.5"
                >
                  <span className="font-bold text-gray-900 flex-shrink-0 w-8 text-right">
                    {item.n}.
                  </span>
                  <span className="flex-shrink-0">{item.title}</span>
                  <span className="flex-1 border-b border-dotted border-gray-300 self-end mb-1"></span>
                  <span
                    className="font-semibold text-gray-700 flex-shrink-0 min-w-[2.5rem] text-right tabular-nums"
                    data-toc-pageref={item.refSection || item.n}
                  >
                    —
                  </span>
                </li>
              ))}
            </ol>
            <p className="text-[9px] text-gray-400 italic mt-5 text-right">
              Página interna do laudo — Viaseg · Assessoria em Medicina Ocupacional
            </p>
          </section>

          {/* 1. Identificação da Empresa */}
          <section className="mb-4 break-inside-avoid" data-toc-section="1">
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
              1. Identificação da Empresa
            </h2>
            <table className="w-full text-xs">
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="py-1 pr-3 font-semibold text-gray-600 w-1/3">Empresa Avaliada</td>
                  <td className="py-1 text-gray-900">{filters.empresa}</td>
                </tr>
                {stats?.company_info?.cnpj && (
                  <tr className="border-b border-gray-200" data-testid="cover-cnpj-row">
                    <td className="py-1 pr-3 font-semibold text-gray-600">CNPJ</td>
                    <td className="py-1 text-gray-900 font-mono">{stats.company_info.cnpj}</td>
                  </tr>
                )}
                <tr className="border-b border-gray-200">
                  <td className="py-1 pr-3 font-semibold text-gray-600">Data de Emissão do Relatório</td>
                  <td className="py-1 text-gray-900">
                    {new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-1 pr-3 font-semibold text-gray-600">Total de Respondentes</td>
                  <td className="py-1 text-gray-900">{stats?.total_assessments || 0} colaboradores</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-1 pr-3 font-semibold text-gray-600">Setores Avaliados</td>
                  <td className="py-1 text-gray-900">
                    {stats?.sector_counts ? Object.keys(stats.sector_counts).length : 0} setores
                    {stats?.sector_counts && Object.keys(stats.sector_counts).length > 0 && (
                      <span className="text-gray-500"> ({Object.keys(stats.sector_counts).join(", ")})</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="py-1 pr-3 font-semibold text-gray-600">Escopo do Diagnóstico</td>
                  <td className="py-1 text-gray-900 font-semibold">Multisetorial — Consolidado da Empresa</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* 2. Objetivo do Diagnóstico (Descrição da Demanda) */}
          <section className="mb-4 break-inside-avoid" data-toc-section="2">
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
              2. Objetivo do Diagnóstico
            </h2>
            <p className="text-xs text-gray-700 leading-relaxed text-justify">
              Este relatório tem por objetivo estruturar e sistematizar a análise dos fatores de riscos
              psicossociais identificados na organização avaliada, classificando-os conforme a gravidade
              percebida pelos colaboradores, de modo a subsidiar a tomada de decisão, a definição de
              prioridades de intervenção e a integração dos resultados ao Programa de Gerenciamento de
              Riscos (PGR), em conformidade com a Norma Regulamentadora nº 01 (NR-01). A análise
              considera as particularidades da organização, seu contexto produtivo, cultura interna,
              modelo de gestão e as características específicas dos setores avaliados.
            </p>
          </section>

          {/* 3. Metodologia COPSOQ II */}
          <section className="mb-4 break-inside-avoid" data-toc-section="3">
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
              3. Metodologia Aplicada — COPSOQ II (Versão Curta)
            </h2>
            <p className="text-xs text-gray-700 leading-relaxed text-justify mb-2">
              A metodologia empregada baseia-se no <strong>Copenhagen Psychosocial Questionnaire (COPSOQ II)</strong>,
              instrumento psicométrico desenvolvido pelo <em>National Research Centre for the Working
              Environment</em> da Dinamarca, internacionalmente reconhecido e validado para a avaliação
              quantitativa de riscos psicossociais no trabalho. Trata-se de uma das ferramentas mais
              utilizadas em estudos científicos e em práticas regulatórias de saúde ocupacional no mundo.
            </p>
            <p className="text-xs text-gray-700 leading-relaxed text-justify mb-2">
              Foi aplicada a <strong>versão curta</strong> do COPSOQ II, composta por 20 questões estruturadas
              em escala Likert de 5 pontos, distribuídas em 5 dimensões centrais. A coleta foi realizada
              de forma <strong>100% anônima e voluntária</strong>, conforme as diretrizes da Lei Geral de Proteção
              de Dados (LGPD) e os princípios éticos do Conselho Federal de Psicologia, garantindo
              segurança psicológica e liberdade de expressão aos respondentes.
            </p>
            <p className="text-xs text-gray-700 leading-relaxed text-justify">
              A classificação dos riscos segue os critérios de severidade estabelecidos pela
              <strong> NR-01</strong>, transpondo as percepções subjetivas dos colaboradores em indicadores
              estatísticos objetivos que conferem ao diagnóstico o respaldo técnico necessário para
              sustentar o gerenciamento de riscos psicossociais e a higidez do meio ambiente de trabalho.
            </p>
          </section>

          {/* 4. Fundamentação da Classificação de Risco (4 níveis NR-01) */}
          <section className="mb-4 break-inside-avoid" data-toc-section="4">
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
              4. Fundamentação da Classificação de Risco
            </h2>
            <p className="text-xs text-gray-700 leading-relaxed text-justify mb-2">
              Os fatores de risco psicossocial foram classificados em <strong>4 (quatro) níveis</strong>,
              alinhados aos critérios de severidade da NR-01, considerando a média ponderada das
              respostas em cada dimensão (escala 1,0 a 5,0):
            </p>
            <table className="w-full text-xs border border-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-300 px-2 py-1 text-left font-bold">Nível</th>
                  <th className="border border-gray-300 px-2 py-1 text-left font-bold">Faixa</th>
                  <th className="border border-gray-300 px-2 py-1 text-left font-bold">Interpretação Técnica</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-2 py-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1 align-middle"></span>
                    <strong>BAIXO</strong>
                  </td>
                  <td className="border border-gray-300 px-2 py-1">1,0 – 2,0</td>
                  <td className="border border-gray-300 px-2 py-1">
                    Ambiente psicossocial saudável. Manter ações preventivas e monitoramento periódico.
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-2 py-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-yellow-600 mr-1 align-middle"></span>
                    <strong>MODERADO</strong>
                  </td>
                  <td className="border border-gray-300 px-2 py-1">2,1 – 3,0</td>
                  <td className="border border-gray-300 px-2 py-1">
                    Riscos atentos. Recomenda-se planejar ações corretivas e reforçar boas práticas.
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-2 py-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-orange-500 mr-1 align-middle"></span>
                    <strong>ALTO</strong>
                  </td>
                  <td className="border border-gray-300 px-2 py-1">3,1 – 4,0</td>
                  <td className="border border-gray-300 px-2 py-1">
                    Riscos relevantes. Intervenções estruturadas devem ser priorizadas e integradas ao PGR.
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-2 py-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1 align-middle"></span>
                    <strong>CRÍTICO</strong>
                  </td>
                  <td className="border border-gray-300 px-2 py-1">4,1 – 5,0</td>
                  <td className="border border-gray-300 px-2 py-1">
                    Risco severo à saúde mental. Ação imediata exigida com planos de mitigação prioritários.
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* 5. Dimensões Avaliadas */}
          <section className="mb-4 break-inside-avoid" data-toc-section="5">
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
              5. Dimensões Avaliadas
            </h2>
            <p className="text-xs text-gray-700 leading-relaxed text-justify mb-2">
              O COPSOQ II (versão curta) investiga 5 dimensões psicossociais reconhecidas pela literatura
              científica como determinantes da saúde mental no trabalho:
            </p>
            <table className="w-full text-xs border border-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-300 px-2 py-1 text-left font-bold w-1/3">Dimensão</th>
                  <th className="border border-gray-300 px-2 py-1 text-left font-bold">Descrição</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-2 py-1 font-semibold">Exigências do Trabalho</td>
                  <td className="border border-gray-300 px-2 py-1">
                    Avalia a carga quantitativa e cognitiva, ritmo de trabalho, demandas emocionais e
                    pressão por prazos. (Escore alto = maior risco)
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-2 py-1 font-semibold">Relações Sociais e Liderança</td>
                  <td className="border border-gray-300 px-2 py-1">
                    Qualidade das relações interpessoais, apoio de colegas e da chefia, clareza de papéis
                    e qualidade da liderança. (Dimensão positiva — escore alto = menor risco)
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-2 py-1 font-semibold">Autonomia</td>
                  <td className="border border-gray-300 px-2 py-1">
                    Grau de controle e influência do colaborador sobre o próprio trabalho, decisões e
                    organização das tarefas. (Dimensão positiva — escore alto = menor risco)
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-2 py-1 font-semibold">Sentido do Trabalho</td>
                  <td className="border border-gray-300 px-2 py-1">
                    Significado, propósito e reconhecimento percebidos na atividade profissional.
                    (Dimensão positiva — escore alto = menor risco)
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-2 py-1 font-semibold">Insegurança e Conflitos</td>
                  <td className="border border-gray-300 px-2 py-1">
                    Percepção de estabilidade no emprego, conflitos interpessoais e exposição a situações
                    de assédio ou desrespeito. (Escore alto = maior risco)
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="text-[10px] text-gray-500 leading-relaxed text-justify mt-2 italic">
              Nota técnica: Para garantir a comparabilidade entre dimensões, o sistema inverte
              matematicamente as escalas das dimensões positivas (Relações, Autonomia e Sentido) ao
              calcular o escore global de risco da empresa.
            </p>
          </section>

          {/* 6. Sumário Executivo (Executive Brief) */}
          {chartData.length > 0 && (() => {
            const overallRisk = getRiskLevel(stats?.overall_score || 0);
            const topCritical = [...chartData]
              .map(d => ({ ...d, effective: d.isPositive ? (6 - d.value) : d.value }))
              .sort((a, b) => b.effective - a.effective)
              .slice(0, 3);
            return (
              <section className="mb-2 break-inside-avoid" data-toc-section="6" data-testid="report-cover-exec-brief">
                <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
                  6. Sumário Executivo
                </h2>
                <p className="text-xs text-gray-700 leading-relaxed text-justify mb-3">
                  Síntese consolidada dos indicadores quantitativos apurados nesta avaliação,
                  destinada à leitura executiva e à tomada de decisão imediata pela alta gestão.
                </p>

                {/* Big numbers row */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {/* Pontuação Geral */}
                  <div className="border border-gray-300 rounded-md p-2">
                    <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">
                      Pontuação Geral da Empresa
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-2xl font-bold" style={{ color: overallRisk.color }}>
                        {(stats?.overall_score || 0).toFixed(2)}
                      </span>
                      <span className="text-xs text-gray-500">/ 5,00</span>
                    </div>
                    <div className="text-[9px] text-gray-500 mt-1">
                      Base de cálculo: média ponderada de todas as dimensões avaliadas
                      (com inversão das escalas positivas).
                    </div>
                  </div>

                  {/* Nível Predominante */}
                  <div className="border border-gray-300 rounded-md p-2">
                    <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">
                      Nível de Risco Predominante
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{ backgroundColor: overallRisk.color }}
                      ></span>
                      <span className="text-lg font-bold" style={{ color: overallRisk.color }}>
                        {overallRisk.level.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-[9px] text-gray-600 mt-1 leading-snug">
                      {overallRisk.level === "Baixo" && "Ambiente psicossocial saudável; manter ações preventivas."}
                      {overallRisk.level === "Moderado" && "Atenção pontual; planejar ações corretivas em pontos focais."}
                      {overallRisk.level === "Alto" && "Intervenções estruturadas devem ser priorizadas e integradas ao PGR."}
                      {overallRisk.level === "Crítico" && "Ação imediata exigida; risco severo à saúde mental dos colaboradores."}
                    </div>
                  </div>
                </div>

                {/* Bottom row: Top 3 + Compact Radar */}
                <div className="grid grid-cols-5 gap-3 mb-2">
                  {/* Top 3 Dimensões Críticas (3/5 width) */}
                  <div className="col-span-3">
                    <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold mb-1">
                      3 Dimensões mais Críticas (Prioridades de Intervenção)
                    </div>
                    <table className="w-full text-xs border border-gray-300">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border border-gray-300 px-1 py-1 text-left font-bold w-5">#</th>
                          <th className="border border-gray-300 px-1 py-1 text-left font-bold">Dimensão</th>
                          <th className="border border-gray-300 px-1 py-1 text-center font-bold w-12">Risco</th>
                          <th className="border border-gray-300 px-1 py-1 text-center font-bold w-16">Nível</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topCritical.map((d, i) => (
                          <tr key={d.name} className={i % 2 === 1 ? "bg-gray-50" : ""}>
                            <td className="border border-gray-300 px-1 py-1 font-bold">{i + 1}</td>
                            <td className="border border-gray-300 px-1 py-1">
                              <div className="font-semibold text-[10px]">{d.name}</div>
                              {d.isPositive && (
                                <div className="text-[8px] text-gray-500 italic leading-tight">
                                  Dimensão positiva — escala invertida
                                </div>
                              )}
                            </td>
                            <td className="border border-gray-300 px-1 py-1 text-center font-bold">
                              {d.effective.toFixed(2)}
                            </td>
                            <td className="border border-gray-300 px-1 py-1 text-center">
                              <span
                                className="inline-block px-1 py-0.5 rounded text-white text-[8px] font-bold uppercase"
                                style={{ backgroundColor: d.riskInfo.color }}
                              >
                                {d.riskInfo.level}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Compact Radar (2/5 width) */}
                  <div className="col-span-2 flex flex-col items-center justify-start">
                    <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold mb-1 w-full text-center">
                      Panorama Geral
                    </div>
                    {(() => {
                      const radarData = chartData.map(d => ({
                        name: d.name,
                        value: d.isPositive ? (6 - d.value) : d.value,
                      }));
                      const numPoints = radarData.length;
                      const centerX = 110;
                      const centerY = 95;
                      const maxRadius = 65;
                      const dataPoints = radarData.map((d, i) => {
                        const angle = (Math.PI * 2 * i / numPoints) - Math.PI / 2;
                        const radius = (d.value / 5) * maxRadius;
                        return {
                          x: centerX + radius * Math.cos(angle),
                          y: centerY + radius * Math.sin(angle),
                          labelX: centerX + (maxRadius + 18) * Math.cos(angle),
                          labelY: centerY + (maxRadius + 18) * Math.sin(angle),
                          ...d,
                        };
                      });
                      const gridLevels = [1, 2, 3, 4, 5];
                      return (
                        <svg width="220" height="190" viewBox="0 0 220 190" className="mx-auto">
                          {gridLevels.map(level => {
                            const r = (level / 5) * maxRadius;
                            const points = Array.from({ length: numPoints }, (_, i) => {
                              const angle = (Math.PI * 2 * i / numPoints) - Math.PI / 2;
                              return `${centerX + r * Math.cos(angle)},${centerY + r * Math.sin(angle)}`;
                            }).join(" ");
                            return <polygon key={level} points={points} fill="none" stroke="#e5e7eb" strokeWidth="1" />;
                          })}
                          {radarData.map((_, i) => {
                            const angle = (Math.PI * 2 * i / numPoints) - Math.PI / 2;
                            return (
                              <line
                                key={i}
                                x1={centerX}
                                y1={centerY}
                                x2={centerX + maxRadius * Math.cos(angle)}
                                y2={centerY + maxRadius * Math.sin(angle)}
                                stroke="#e5e7eb"
                                strokeWidth="1"
                              />
                            );
                          })}
                          <polygon
                            points={dataPoints.map(p => `${p.x},${p.y}`).join(" ")}
                            fill="#f97316"
                            fillOpacity="0.35"
                            stroke="#f97316"
                            strokeWidth="1.5"
                          />
                          {dataPoints.map((p, i) => (
                            <circle key={i} cx={p.x} cy={p.y} r="2.5" fill="#f97316" stroke="#fff" strokeWidth="0.8" />
                          ))}
                          {dataPoints.map((p, i) => {
                            const truncated = p.name.length > 14 ? p.name.substring(0, 12) + "…" : p.name;
                            const textAnchor =
                              p.labelX < centerX - 5 ? "end" : p.labelX > centerX + 5 ? "start" : "middle";
                            return (
                              <text
                                key={i}
                                x={p.labelX}
                                y={p.labelY}
                                textAnchor={textAnchor}
                                dominantBaseline="middle"
                                fontSize="6.5"
                                fill="#374151"
                                fontWeight="600"
                              >
                                {truncated}
                              </text>
                            );
                          })}
                          <text x={centerX} y={centerY - maxRadius - 4} textAnchor="middle" fontSize="6" fill="#9ca3af">5</text>
                        </svg>
                      );
                    })()}
                    <div className="text-[8px] text-gray-500 italic text-center leading-snug w-full px-1">
                      Eixo: 0 (baixo risco) → 5 (alto risco). Escalas positivas invertidas.
                    </div>
                  </div>
                </div>

                <p className="text-[9px] text-gray-500 italic leading-snug">
                  As páginas a seguir apresentam o detalhamento técnico por dimensão, distribuições
                  estatísticas, comparativo entre setores e a análise interpretativa completa.
                </p>
              </section>
            );
          })()}

          {/* Page break before the rest of the report */}
          <div className="report-cover-pagebreak"></div>
        </div>
      )}

      <div ref={reportRef} className="max-w-7xl mx-auto p-6 print:p-2 space-y-6 print:space-y-3">

        {/* Resumo Executivo */}
        <div className="bg-white rounded-xl p-6 print:p-4 border border-gray-200 print:break-inside-avoid print:border-0">
          <div className="block mb-2 break-inside-avoid" data-toc-section="7">
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
              {filters.empresa && !filters.setor ? "7. " : "1. "}Resumo Executivo
            </h2>
          </div>

          {/* Show message when no company is selected */}
          {!filters.empresa ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Building2 className="w-16 h-16 text-gray-300 mb-4" />
              <h4 className="text-lg font-semibold text-gray-500 mb-2">Selecione uma Empresa</h4>
              <p className="text-sm text-gray-400 max-w-md">
                Utilize o filtro "Empresa / Unidade" acima para visualizar o resumo executivo de uma empresa específica.
              </p>
            </div>
          ) : (() => {
            const overallRisk = getRiskLevel(stats?.overall_score || 0);
            const adhesionRate = stats?.numero_funcionarios > 0
              ? Math.round(((stats?.total_assessments || 0) / stats.numero_funcionarios) * 100)
              : null;
            const nextDate = stats?.first_assessment_date
              ? (() => {
                  const d = new Date(stats.first_assessment_date);
                  d.setFullYear(d.getFullYear() + 1);
                  return d.toLocaleDateString("pt-BR");
                })()
              : "Aguardando início";
            return (
              <>
                <p className="text-xs text-gray-700 leading-relaxed text-justify mb-3">
                  Síntese das informações cadastrais da empresa avaliada, contemplando a base de
                  participação, a distribuição setorial das respostas e o cronograma de aplicação
                  do instrumento de avaliação psicossocial.
                </p>

                {/* Big numbers row - 4 columns */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                  {/* Empresa Avaliada */}
                  <div className="border border-gray-300 rounded-md p-2">
                    <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">
                      Empresa Avaliada
                    </div>
                    <div className="text-sm font-bold text-gray-900 mt-1 leading-tight" data-testid="resumo-empresa-nome">
                      {filters.empresa}
                    </div>
                    <div className="text-[9px] text-gray-500 mt-1">
                      Unidade base do diagnóstico psicossocial.
                    </div>
                  </div>

                  {/* Funcionários */}
                  <div className="border border-gray-300 rounded-md p-2">
                    <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">
                      Funcionários
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-2xl font-bold text-purple-600" data-testid="resumo-funcionarios">
                        {stats?.numero_funcionarios > 0 ? stats.numero_funcionarios : "—"}
                      </span>
                    </div>
                    <div className="text-[9px] text-gray-500 mt-1">
                      Total de colaboradores cadastrados na unidade.
                    </div>
                  </div>

                  {/* Total de Respostas */}
                  <div className="border border-gray-300 rounded-md p-2">
                    <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">
                      Total de Respostas
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-2xl font-bold text-orange-600" data-testid="resumo-total-respostas">
                        {stats?.total_assessments || 0}
                      </span>
                      {stats?.numero_funcionarios > 0 && (
                        <span className="text-xs text-gray-500">/ {stats.numero_funcionarios}</span>
                      )}
                      {adhesionRate !== null && (
                        <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${
                          adhesionRate >= 80
                            ? "bg-green-100 text-green-700"
                            : adhesionRate >= 50
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-red-100 text-red-700"
                        }`}>
                          {adhesionRate}%
                        </span>
                      )}
                    </div>
                    <div className="text-[9px] text-gray-500 mt-1">
                      {adhesionRate !== null
                        ? "Taxa de adesão dos colaboradores."
                        : "Questionários completos recebidos."}
                    </div>
                  </div>

                  {/* Pontuação Geral */}
                  <div className="border border-gray-300 rounded-md p-2">
                    <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">
                      Pontuação Geral
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-2xl font-bold" style={{ color: overallRisk.color }} data-testid="resumo-pontuacao">
                        {(stats?.overall_score || 0).toFixed(2)}
                      </span>
                      <span className="text-xs text-gray-500">/ 5,00</span>
                    </div>
                    <div className="text-[9px] text-gray-500 mt-1">
                      Média ponderada de todas as dimensões.
                    </div>
                  </div>
                </div>

                {/* Bottom row: Sectors table (3/5) + Dates panel (2/5) */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 mb-2">
                  {/* Respostas por Setor */}
                  <div className="lg:col-span-3">
                    <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold mb-1">
                      Respostas por Setor
                    </div>
                    {stats?.sector_counts && Object.keys(stats.sector_counts).length > 0 ? (
                      <table className="w-full text-xs border border-gray-300">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="border border-gray-300 px-1 py-1 text-left font-bold w-5">#</th>
                            <th className="border border-gray-300 px-1 py-1 text-left font-bold">Setor</th>
                            <th className="border border-gray-300 px-1 py-1 text-center font-bold w-16">Respostas</th>
                            <th className="border border-gray-300 px-1 py-1 text-center font-bold w-16">Participação</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const entries = Object.entries(stats.sector_counts);
                            const total = entries.reduce((s, [, c]) => s + (c || 0), 0) || 1;
                            return entries.map(([sector, count], i) => {
                              const pct = Math.round((count / total) * 100);
                              return (
                                <tr key={sector} className={i % 2 === 1 ? "bg-gray-50" : ""}>
                                  <td className="border border-gray-300 px-1 py-1 font-bold">{i + 1}</td>
                                  <td className="border border-gray-300 px-1 py-1">
                                    <div className="font-semibold text-[10px]">{sector}</div>
                                  </td>
                                  <td className="border border-gray-300 px-1 py-1 text-center font-bold">
                                    {count}
                                  </td>
                                  <td className="border border-gray-300 px-1 py-1 text-center">
                                    <span className="inline-block px-1 py-0.5 rounded bg-orange-100 text-orange-700 text-[9px] font-bold">
                                      {pct}%
                                    </span>
                                  </td>
                                </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-[10px] text-gray-400 italic">Nenhum dado por setor disponível.</p>
                    )}
                  </div>

                  {/* Cronograma de Aplicação */}
                  <div className="lg:col-span-2">
                    <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold mb-1">
                      Cronograma de Aplicação
                    </div>
                    <div className="space-y-2">
                      <div className="border border-gray-300 rounded-md p-2">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3 h-3 text-green-600" />
                          <span className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">
                            Início das Avaliações
                          </span>
                        </div>
                        <div className="text-sm font-bold text-gray-900 mt-1">
                          {stats?.first_assessment_date
                            ? new Date(stats.first_assessment_date).toLocaleDateString("pt-BR")
                            : "Sem registros"}
                        </div>
                      </div>

                      <div className="border border-gray-300 rounded-md p-2">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3 h-3 text-orange-600" />
                          <span className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">
                            Próxima Avaliação Sugerida
                          </span>
                        </div>
                        <div className="text-sm font-bold text-orange-600 mt-1">
                          {nextDate}
                        </div>
                      </div>

                      {stats?.last_assessment_date && (
                        <div className="border border-gray-300 rounded-md p-2">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3 h-3 text-blue-600" />
                            <span className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">
                              Última Resposta
                            </span>
                          </div>
                          <div className="text-sm font-bold text-gray-700 mt-1">
                            {new Date(stats.last_assessment_date).toLocaleDateString("pt-BR")}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-[9px] text-gray-500 italic leading-snug">
                  As seções a seguir apresentam o detalhamento técnico por dimensão, distribuições
                  estatísticas, comparativo entre setores e a análise interpretativa completa.
                </p>
              </>
            );
          })()}
        </div>

        {/* Análise Quantitativa de Riscos */}
        <div className="bg-white rounded-xl p-6 print:p-4 border border-gray-200 print:border-0 print:break-inside-avoid">
          <div className="block mb-2 break-inside-avoid" data-toc-section="8">
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
              {filters.empresa && !filters.setor ? "8. " : "2. "}Análise Quantitativa de Riscos
            </h2>
          </div>
          <p className="text-xs text-gray-700 leading-relaxed text-justify mb-3">
            Identificação técnica e protocolar do recorte da análise quantitativa apresentada nas
            seções subsequentes — empresa, unidade/setor e grupo homogêneo de exposição (GHE)
            considerados para todas as métricas, gráficos e interpretações deste relatório.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="border border-gray-300 rounded-md p-2">
              <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">
                Empresa
              </div>
              <div className="text-sm font-bold text-gray-900 mt-1 leading-tight" data-testid="aqr-empresa">
                {filters.empresa || "Consolidado Geral"}
              </div>
              <div className="text-[9px] text-gray-500 mt-1">
                Recorte primário do diagnóstico psicossocial.
              </div>
            </div>
            <div className="border border-gray-300 rounded-md p-2">
              <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">
                Unidade / Setor
              </div>
              <div className="text-sm font-bold text-gray-900 mt-1 leading-tight" data-testid="aqr-setor">
                {filters.setor || "Multisetorial"}
              </div>
              <div className="text-[9px] text-gray-500 mt-1">
                Escopo organizacional considerado na amostra.
              </div>
            </div>
            <div className="border border-gray-300 rounded-md p-2">
              <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">
                GHE / Função
              </div>
              <div className="text-sm font-bold text-gray-900 mt-1 leading-tight" data-testid="aqr-funcao">
                {filters.funcao || "Operacional Consolidado"}
              </div>
              <div className="text-[9px] text-gray-500 mt-1">
                Grupo homogêneo de exposição avaliado.
              </div>
            </div>
          </div>
        </div>

        {/* Mensagem quando nenhuma empresa selecionada */}
        {!filters.empresa && (
          <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
            <Building2 className="w-20 h-20 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-500 mb-2">Selecione uma Empresa</h3>
            <p className="text-gray-400 max-w-md mx-auto">
              Utilize o filtro "Empresa / Unidade" no painel acima para visualizar a análise quantitativa de riscos de uma empresa específica.
            </p>
          </div>
        )}

        {/* Escala de Gravidade Global */}
        <div className={`bg-white rounded-xl p-6 print:p-4 border border-gray-200 print:border-0 print:break-inside-avoid ${!filters.empresa ? 'hidden' : ''}`}>
          <div className="block mb-2 break-inside-avoid" data-toc-section="9">
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
              {filters.empresa && !filters.setor ? "9. " : "3. "}Escala de Gravidade Global
            </h2>
          </div>
          <p className="text-xs text-gray-700 leading-relaxed text-justify mb-3">
            Distribuição percentual consolidada dos riscos identificados em todas as dimensões
            avaliadas, oferecendo um termômetro geral da saúde psicossocial da organização ou
            do setor filtrado.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:gap-4 items-center">
            <div className="flex items-center justify-center" style={{ minHeight: '200px' }}>
              {gravityDistribution.length > 0 ? (
                <>
                  {/* Recharts Pie - Screen only */}
                  <div style={{ width: '100%', height: '200px' }} className="print:hidden">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={gravityDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                        >
                          {gravityDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* CSS Pie Chart - Print only */}
                  <div className="hidden print:block">
                    <PrintPieChart data={gravityDistribution} />
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400 italic">Sem dados para exibição.</p>
                </div>
              )}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold mb-2">
                Interpretação da Escala
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="border border-gray-300 rounded-md p-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0"></span>
                    <span className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">Baixo</span>
                  </div>
                  <div className="text-sm font-bold text-green-600 mt-0.5">1,0 – 2,0</div>
                  <div className="text-[9px] text-gray-500">Ambiente saudável.</div>
                </div>
                <div className="border border-gray-300 rounded-md p-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-600 flex-shrink-0"></span>
                    <span className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">Moderado</span>
                  </div>
                  <div className="text-sm font-bold text-yellow-700 mt-0.5">2,1 – 3,0</div>
                  <div className="text-[9px] text-gray-500">Atenção pontual.</div>
                </div>
                <div className="border border-gray-300 rounded-md p-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                    <span className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">Alto</span>
                  </div>
                  <div className="text-sm font-bold text-orange-600 mt-0.5">3,1 – 4,0</div>
                  <div className="text-[9px] text-gray-500">Intervenção prioritária.</div>
                </div>
                <div className="border border-gray-300 rounded-md p-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0"></span>
                    <span className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">Crítico</span>
                  </div>
                  <div className="text-sm font-bold text-red-600 mt-0.5">4,1 – 5,0</div>
                  <div className="text-[9px] text-gray-500">Ação imediata exigida.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comparativo entre Setores */}
        {filters.empresa && stats?.sector_comparison && stats.sector_comparison.length > 1 && !filters.setor && (
          <div className="bg-white rounded-xl p-6 print:p-4 border border-gray-200 print:border-0 print:break-inside-avoid">
            <div className="block mb-2 break-inside-avoid" data-toc-section="10">
              <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
                10. Comparativo entre Setores
                <span className="ml-3 text-xs text-gray-500 normal-case font-semibold tracking-normal">
                  ({stats.sector_comparison.length} setores)
                </span>
              </h2>
            </div>
            <p className="text-xs text-gray-700 leading-relaxed text-justify mb-3">
              Ranking dos setores avaliados, ordenados do maior para o menor nível de risco
              psicossocial. Permite priorizar intervenções e identificar áreas críticas que
              demandam atenção imediata.
            </p>

            {/* Recharts - Screen only */}
            <div className="print:hidden" style={{ width: '100%', height: Math.max(200, stats.sector_comparison.length * 50) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stats.sector_comparison}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" domain={[0, 5]} tickCount={6} tick={{ fontSize: 11 }} />
                  <YAxis 
                    dataKey="setor" 
                    type="category" 
                    width={120} 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => value.length > 15 ? value.substring(0, 15) + '...' : value}
                  />
                  <Tooltip 
                    formatter={(value, name) => [value.toFixed(2), 'Pontuação de Risco']}
                    labelFormatter={(label) => `Setor: ${label}`}
                    contentStyle={{ fontSize: '12px' }}
                  />
                  <Bar dataKey="overall_risk" radius={[0, 4, 4, 0]}>
                    {stats.sector_comparison.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.risk_color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* CSS Bar Chart - Print only */}
            <div className="hidden print:block space-y-2">
              {stats.sector_comparison.map((sector, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-[9px] text-gray-600 w-24 truncate">{sector.setor}</span>
                  <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                    <div 
                      className="h-full rounded transition-all"
                      style={{ 
                        width: `${(sector.overall_risk / 5) * 100}%`, 
                        backgroundColor: sector.risk_color 
                      }}
                    />
                  </div>
                  <span className="text-[9px] font-bold w-10 text-right" style={{ color: sector.risk_color }}>
                    {sector.overall_risk.toFixed(1)}
                  </span>
                  <span 
                    className="text-[7px] px-1.5 py-0.5 rounded text-white font-semibold"
                    style={{ backgroundColor: sector.risk_color }}
                  >
                    {sector.risk_level}
                  </span>
                </div>
              ))}
            </div>

            {/* Sector Detail Cards - Screen only (standardized to bordered KPI boxes) */}
            <div className="mt-4 print:hidden">
              <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold mb-2">
                Setores em Destaque
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {stats.sector_comparison.slice(0, 6).map((sector, idx) => (
                  <div
                    key={idx}
                    className="border border-gray-300 rounded-md p-2 hover:shadow-sm transition-shadow cursor-pointer"
                    onClick={() => setFilters({ ...filters, setor: sector.setor })}
                    data-testid={`sector-comparison-card-${idx}`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold leading-tight">
                        {sector.setor}
                      </div>
                      <span
                        className="px-1.5 py-0.5 rounded text-white text-[8px] font-bold uppercase flex-shrink-0"
                        style={{ backgroundColor: sector.risk_color }}
                      >
                        {sector.risk_level}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-2xl font-bold" style={{ color: sector.risk_color }}>
                        {sector.overall_risk.toFixed(2)}
                      </span>
                      <span className="text-xs text-gray-500">/ 5,00</span>
                    </div>
                    <div className="text-[9px] text-gray-500 mt-1">
                      {sector.response_count} {sector.response_count === 1 ? 'resposta' : 'respostas'} · Clique para filtrar
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {stats.sector_comparison.length > 6 && (
              <p className="text-[9px] text-gray-500 italic mt-2 print:hidden">
                Exibindo os 6 setores com maior risco. Utilize o painel de filtros para visualizar todos.
              </p>
            )}
          </div>
        )}

        {/* Métricas Detalhadas por Nível */}
        <div className={`bg-white rounded-xl p-6 print:p-4 border border-gray-200 print:border-0 ${!filters.empresa ? 'hidden' : ''}`}>
          <div className="block mb-2 break-inside-avoid" data-toc-section={filters.empresa && !filters.setor ? (stats?.sector_comparison && stats.sector_comparison.length > 1 ? "11" : "10") : "4"}>
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
              {filters.empresa && !filters.setor
                ? (stats?.sector_comparison && stats.sector_comparison.length > 1 ? "11. " : "10. ")
                : "4. "}
              Métricas Detalhadas por Nível
            </h2>
          </div>
          <p className="text-xs text-gray-700 leading-relaxed text-justify mb-3">
            Detalhamento das dimensões avaliadas, segmentadas em dois agrupamentos operacionais:
            <strong> Controle</strong> (monitoramento contínuo, indicadores estáveis) e
            <strong> Prioridades de Intervenção</strong> (ações corretivas requeridas no curto prazo).
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:gap-4">
            {/* Controle */}
            <div>
              <div className="text-[10px] uppercase tracking-wide text-blue-600 font-bold mb-2 flex items-center gap-1.5">
                <Eye className="w-3 h-3" />
                Controle · Monitoramento
              </div>
              <div className="space-y-3">
                {controlMetrics.length > 0 ? (
                  controlMetrics.map((item, idx) => (
                    <DimensionCard key={idx} dimension={item} />
                  ))
                ) : (
                  <p className="text-[10px] text-gray-400 italic text-center py-4">Nenhum dado em nível de controle.</p>
                )}
              </div>
            </div>

            {/* Prioridades de Intervenção */}
            <div>
              <div className="text-[10px] uppercase tracking-wide text-orange-600 font-bold mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3" />
                Prioridades de Intervenção
              </div>
              <div className="space-y-3">
                {interventionMetrics.length > 0 ? (
                  interventionMetrics.map((item, idx) => (
                    <DimensionCard key={idx} dimension={item} isIntervention />
                  ))
                ) : (
                  <div className="border border-green-200 rounded-md p-3 bg-green-50/50 text-center">
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-xs text-green-700 font-semibold">Nenhuma intervenção prioritária necessária</p>
                    <p className="text-[10px] text-green-600 mt-1">Todos os indicadores em níveis aceitáveis.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Panorama Geral (Radar) */}
        {filters.empresa && chartData.length > 0 && (
          <div className="bg-white rounded-xl p-6 print:p-4 border border-gray-200 print:border-0 print:break-inside-avoid">
            <div className="block mb-2 break-inside-avoid" data-toc-section={filters.empresa && !filters.setor ? (stats?.sector_comparison && stats.sector_comparison.length > 1 ? "12" : "11") : "5"}>
              <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
                {filters.empresa && !filters.setor
                  ? (stats?.sector_comparison && stats.sector_comparison.length > 1 ? "12. " : "11. ")
                  : "5. "}
                Panorama Geral (Radar)
              </h2>
            </div>
            <p className="text-xs text-gray-700 leading-relaxed text-justify mb-3">
              Visualização comparativa de todas as dimensões avaliadas em formato radar.
              Quanto mais próximo da borda externa, maior o nível de risco identificado;
              escalas positivas são apresentadas com inversão para facilitar a leitura.
            </p>

            {/* Recharts Radar - Screen only */}
            <div className="print:hidden flex justify-center" style={{ width: '100%', height: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={chartData.map(d => ({
                  ...d,
                  // Use effective risk score for radar
                  riskScore: isPositiveDimension(d.name) ? (6 - d.value) : d.value,
                  fullMark: 5
                }))}>
                  <PolarGrid gridType="polygon" stroke="#d1d5db" />
                  <PolarAngleAxis 
                    dataKey="name" 
                    tick={{ fontSize: 10, fill: '#374151' }}
                    tickFormatter={(value) => {
                      // Truncate long names for print readability
                      if (value.length > 18) {
                        return value.substring(0, 16) + '...';
                      }
                      return value;
                    }}
                  />
                  <PolarRadiusAxis 
                    angle={90} 
                    domain={[0, 5]} 
                    tick={{ fontSize: 8, fill: '#6b7280' }}
                    tickCount={6}
                  />
                  <Radar
                    name="Nível de Risco"
                    dataKey="riskScore"
                    stroke="#6366f1"
                    fill="#6366f1"
                    fillOpacity={0.4}
                    strokeWidth={2}
                  />
                  <Tooltip 
                    formatter={(value, name, props) => {
                      const riskInfo = getRiskLevel(value);
                      return [`${value.toFixed(2)} - ${riskInfo.level}`, 'Pontuação de Risco'];
                    }}
                    labelFormatter={(label) => label}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* CSS Radar Chart - Print only */}
            <div className="hidden print:flex print:flex-col print:items-center">
              {(() => {
                const radarData = chartData.map(d => ({
                  name: d.name,
                  value: isPositiveDimension(d.name) ? (6 - d.value) : d.value,
                  riskInfo: getRiskLevel(isPositiveDimension(d.name) ? (6 - d.value) : d.value)
                }));
                const numPoints = radarData.length;
                const centerX = 150;
                const centerY = 130;
                const maxRadius = 100;
                
                // Generate polygon points for the data
                const dataPoints = radarData.map((d, i) => {
                  const angle = (Math.PI * 2 * i / numPoints) - Math.PI / 2;
                  const radius = (d.value / 5) * maxRadius;
                  return {
                    x: centerX + radius * Math.cos(angle),
                    y: centerY + radius * Math.sin(angle),
                    labelX: centerX + (maxRadius + 25) * Math.cos(angle),
                    labelY: centerY + (maxRadius + 25) * Math.sin(angle),
                    ...d
                  };
                });
                
                // Generate grid circles
                const gridLevels = [1, 2, 3, 4, 5];
                
                return (
                  <svg width="320" height="300" viewBox="0 0 320 300" className="mx-auto">
                    {/* Grid circles */}
                    {gridLevels.map(level => {
                      const r = (level / 5) * maxRadius;
                      const points = Array.from({length: numPoints}, (_, i) => {
                        const angle = (Math.PI * 2 * i / numPoints) - Math.PI / 2;
                        return `${centerX + r * Math.cos(angle)},${centerY + r * Math.sin(angle)}`;
                      }).join(' ');
                      return (
                        <polygon
                          key={level}
                          points={points}
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="1"
                        />
                      );
                    })}
                    
                    {/* Axis lines */}
                    {radarData.map((_, i) => {
                      const angle = (Math.PI * 2 * i / numPoints) - Math.PI / 2;
                      return (
                        <line
                          key={i}
                          x1={centerX}
                          y1={centerY}
                          x2={centerX + maxRadius * Math.cos(angle)}
                          y2={centerY + maxRadius * Math.sin(angle)}
                          stroke="#e5e7eb"
                          strokeWidth="1"
                        />
                      );
                    })}
                    
                    {/* Data polygon */}
                    <polygon
                      points={dataPoints.map(p => `${p.x},${p.y}`).join(' ')}
                      fill="#6366f1"
                      fillOpacity="0.4"
                      stroke="#6366f1"
                      strokeWidth="2"
                    />
                    
                    {/* Data points */}
                    {dataPoints.map((p, i) => (
                      <circle
                        key={i}
                        cx={p.x}
                        cy={p.y}
                        r="4"
                        fill="#6366f1"
                        stroke="#fff"
                        strokeWidth="1"
                      />
                    ))}
                    
                    {/* Labels */}
                    {dataPoints.map((p, i) => {
                      const truncatedName = p.name.length > 15 ? p.name.substring(0, 13) + '...' : p.name;
                      const textAnchor = p.labelX < centerX - 10 ? 'end' : p.labelX > centerX + 10 ? 'start' : 'middle';
                      return (
                        <text
                          key={i}
                          x={p.labelX}
                          y={p.labelY}
                          textAnchor={textAnchor}
                          dominantBaseline="middle"
                          fontSize="8"
                          fill="#374151"
                          fontWeight="500"
                        >
                          {truncatedName}
                        </text>
                      );
                    })}
                    
                    {/* Center label */}
                    <text x={centerX} y={centerY - 8} textAnchor="middle" fontSize="7" fill="#9ca3af">0</text>
                    <text x={centerX} y={centerY - maxRadius - 5} textAnchor="middle" fontSize="7" fill="#9ca3af">5</text>
                  </svg>
                );
              })()}
              
              {/* Legend */}
              <div className="flex items-center gap-2 mt-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#6366f1', opacity: 0.6 }}></div>
                <span className="text-[9px] text-gray-600">Nível de Risco</span>
              </div>
            </div>

            {/* Values table below radar for print clarity */}
            <div className="hidden print:block mt-4 pt-3 border-t border-gray-200">
              <p className="text-[9px] font-semibold text-gray-500 mb-2 uppercase">Valores por Dimensão:</p>
              <div className="grid grid-cols-3 gap-2">
                {chartData.map((d, idx) => {
                  const effectiveRisk = isPositiveDimension(d.name) ? (6 - d.value) : d.value;
                  const riskInfo = getRiskLevel(effectiveRisk);
                  
                  return (
                    <div key={idx} className="flex items-center gap-1.5 text-[8px]">
                      <span 
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: riskInfo.color }}
                      />
                      <span className="text-gray-600 truncate">{d.name}:</span>
                      <span className="font-bold" style={{ color: riskInfo.color }}>
                        {effectiveRisk.toFixed(1)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend for risk levels (standardized) */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold mb-2">
                Escala de Risco
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="border border-gray-300 rounded-md p-2 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0"></span>
                  <div>
                    <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">Baixo</div>
                    <div className="text-xs font-bold text-green-600">1,0 – 2,0</div>
                  </div>
                </div>
                <div className="border border-gray-300 rounded-md p-2 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-600 flex-shrink-0"></span>
                  <div>
                    <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">Moderado</div>
                    <div className="text-xs font-bold text-yellow-700">2,1 – 3,0</div>
                  </div>
                </div>
                <div className="border border-gray-300 rounded-md p-2 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                  <div>
                    <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">Alto</div>
                    <div className="text-xs font-bold text-orange-600">3,1 – 4,0</div>
                  </div>
                </div>
                <div className="border border-gray-300 rounded-md p-2 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0"></span>
                  <div>
                    <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">Crítico</div>
                    <div className="text-xs font-bold text-red-600">4,1 – 5,0</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Análise de IA Section */}
        <div ref={aiSectionRef} className={`bg-white rounded-xl p-6 print:p-4 border border-gray-200 print:border-0 print:break-inside-avoid ${!filters.empresa ? 'hidden' : ''}`}>
          <div className="block mb-2 break-inside-avoid" data-toc-section={filters.empresa && !filters.setor ? (stats?.sector_comparison && stats.sector_comparison.length > 1 ? "13" : "12") : "6"}>
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
              {filters.empresa && !filters.setor
                ? (stats?.sector_comparison && stats.sector_comparison.length > 1 ? "13. " : "12. ")
                : "6. "}
              Análise de Riscos — Interpretação e Conclusão
            </h2>
          </div>
          <p className="text-xs text-gray-700 leading-relaxed text-justify mb-3">
            Síntese interpretativa elaborada a partir dos dados consolidados nas seções
            anteriores. Apresenta diagnóstico geral, sugestões de controle, ações prioritárias
            e conclusão técnica para suporte à tomada de decisão.
          </p>
          <div className="flex flex-wrap items-end gap-2 mb-4 print:hidden">
            <div className="flex flex-col">
              <label className="text-[9px] uppercase tracking-wide font-semibold text-gray-600 mb-1">
                Modelo de IA
              </label>
              <div className="relative">
                <select
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                  disabled={loadingAI}
                  data-testid="ai-model-selector"
                  className="appearance-none pl-3 pr-8 h-9 rounded-lg border border-gray-300 bg-white text-xs font-semibold text-gray-700 hover:border-indigo-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {AI_MODEL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
            <button
              onClick={() => setShowAiHistory(true)}
              data-testid="ai-history-btn"
              className="flex items-center gap-2 px-4 h-9 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold text-xs"
            >
              <History className="w-3.5 h-3.5" />
              HISTÓRICO ({aiHistory.length})
            </button>
            <button
              onClick={handleAIAnalysis}
              disabled={loadingAI || !stats || stats.total_assessments === 0}
              data-testid="generate-ai-analysis-btn"
              className="flex items-center gap-2 px-4 h-9 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all font-semibold text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingAI ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  Analisando...
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5" />
                  GERAR ANÁLISE
                </>
              )}
            </button>
          </div>

          {aiError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-xs print:hidden">
              {aiError}
            </div>
          )}

          {aiAnalysis ? (
            <div className="space-y-3">
              {aiAnalysis.ai_model_label && (
                <div className="print:hidden flex items-center gap-2 text-[11px] text-gray-500" data-testid="current-ai-model-badge">
                  <span>Análise gerada com</span>
                  <span className={`px-2 py-0.5 rounded border font-bold ${getModelMeta(aiAnalysis.ai_model).badgeClass}`}>
                    <Sparkles className="w-3 h-3 inline mr-1 -mt-0.5" />
                    {aiAnalysis.ai_model_label}
                  </span>
                </div>
              )}
              <div className="border border-gray-300 rounded-md p-3">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <FileText className="w-3.5 h-3.5 text-indigo-600" />
                  <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold">
                    Interpretação Geral
                  </div>
                </div>
                <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-line text-justify">
                  {aiAnalysis.interpretation}
                </p>
              </div>

              <div className="border border-gray-300 rounded-md p-3">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold">
                    Sugestões de Controle
                  </div>
                </div>
                <ul className="space-y-1.5">
                  {aiAnalysis.control_suggestions.map((suggestion, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-gray-700 leading-relaxed">
                      <span className="w-4 h-4 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 mt-0.5">
                        {idx + 1}
                      </span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border border-gray-300 rounded-md p-3">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-orange-600" />
                  <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold">
                    Ações Prioritárias
                  </div>
                </div>
                <ul className="space-y-1.5">
                  {aiAnalysis.priority_actions.map((action, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-gray-700 leading-relaxed">
                      <span className="w-4 h-4 bg-orange-100 text-orange-700 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 mt-0.5">
                        {idx + 1}
                      </span>
                      <span>{action}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border border-gray-300 rounded-md p-3">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                  <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold">
                    Conclusão
                  </div>
                </div>
                <p className="text-xs text-gray-700 leading-relaxed text-justify">
                  {aiAnalysis.general_recommendations}
                </p>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-gray-300 rounded-md p-6 text-center print:hidden">
              <Sparkles className="w-8 h-8 text-indigo-300 mx-auto mb-2" />
              <p className="text-xs text-gray-500 mb-1">Clique em "Gerar Análise" para obter interpretações e sugestões de controle.</p>
              <p className="text-[10px] text-gray-400">A análise será gerada com base nos dados filtrados acima.</p>
            </div>
          )}
        </div>

        {/* === Relatórios Anteriores (Timeline) — Screen only === */}
        {filters.empresa && (
          <div className="bg-white rounded-xl p-6 border border-gray-200 print:hidden" data-testid="emissions-timeline-section">
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
              Relatórios Anteriores · Evolução no Tempo
            </h2>
            <p className="text-xs text-gray-700 leading-relaxed text-justify mb-3">
              Histórico de emissões deste laudo para a empresa selecionada. Permite acompanhar
              a evolução dos riscos psicossociais ao longo do tempo, ideal para demonstrar à
              diretoria o progresso obtido após intervenções no PGR.
            </p>

            {loadingEmissions ? (
              <div className="flex items-center gap-2 text-xs text-gray-500 py-4">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Carregando histórico…
              </div>
            ) : emissionHistory.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-md p-6 text-center" data-testid="emissions-empty-state">
                <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-500 mb-1">Nenhum relatório emitido anteriormente.</p>
                <p className="text-[10px] text-gray-400">
                  Cada vez que você clicar em <strong>IMPRIMIR RELATÓRIO</strong>, um registro
                  será automaticamente arquivado aqui.
                </p>
              </div>
            ) : (() => {
              // Ordered chronologically (oldest → newest) for chart; latest first for list
              const chronoAsc = [...emissionHistory].slice().reverse();
              const chartSeries = chronoAsc.map((em) => ({
                label: new Date(em.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "2-digit" }),
                score: Number(em.overall_score) || 0,
                respostas: em.total_assessments || 0,
              }));
              const latest = emissionHistory[0];
              const previous = emissionHistory[1];
              const delta = previous ? +(latest.overall_score - previous.overall_score).toFixed(2) : null;

              return (
                <>
                  {/* KPI strip */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="border border-gray-300 rounded-md p-2">
                      <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">Emissões totais</div>
                      <div className="text-2xl font-bold text-gray-900 mt-1" data-testid="emissions-total-count">
                        {emissionHistory.length}
                      </div>
                      <div className="text-[9px] text-gray-500 mt-1">Relatórios arquivados.</div>
                    </div>
                    <div className="border border-gray-300 rounded-md p-2">
                      <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">Última emissão</div>
                      <div className="text-sm font-bold text-gray-900 mt-1">
                        {new Date(latest.created_at).toLocaleDateString("pt-BR")}
                      </div>
                      <div className="text-[9px] text-gray-500 mt-1">
                        por {latest.issued_by || "—"}
                      </div>
                    </div>
                    <div className="border border-gray-300 rounded-md p-2">
                      <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">Pontuação Geral atual</div>
                      <div className="text-2xl font-bold text-gray-900 mt-1">
                        {Number(latest.overall_score).toFixed(2)}
                        <span className="text-xs text-gray-500"> /5,00</span>
                      </div>
                      <div className="text-[9px] text-gray-500 mt-1">Snapshot da emissão.</div>
                    </div>
                    <div className="border border-gray-300 rounded-md p-2">
                      <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">Tendência vs. anterior</div>
                      {delta === null ? (
                        <div className="text-sm font-bold text-gray-400 mt-1 flex items-center gap-1">
                          <Minus className="w-3 h-3" /> Sem comparativo
                        </div>
                      ) : delta < 0 ? (
                        <div className="text-sm font-bold text-green-600 mt-1 flex items-center gap-1">
                          <TrendingDown className="w-3.5 h-3.5" /> {delta.toFixed(2)}
                        </div>
                      ) : delta > 0 ? (
                        <div className="text-sm font-bold text-red-600 mt-1 flex items-center gap-1">
                          <TrendingUp className="w-3.5 h-3.5" /> +{delta.toFixed(2)}
                        </div>
                      ) : (
                        <div className="text-sm font-bold text-gray-700 mt-1 flex items-center gap-1">
                          <Minus className="w-3.5 h-3.5" /> Estável
                        </div>
                      )}
                      <div className="text-[9px] text-gray-500 mt-1">
                        {delta === null ? "Primeira emissão." : delta < 0 ? "Risco reduziu — bom progresso." : delta > 0 ? "Risco aumentou — atenção." : "Sem variação."}
                      </div>
                    </div>
                  </div>

                  {/* Line chart */}
                  {chartSeries.length >= 2 && (
                    <div className="border border-gray-200 rounded-md p-3 mb-4">
                      <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold mb-2">
                        Evolução da Pontuação Geral
                      </div>
                      <div style={{ width: "100%", height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartSeries} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                            <YAxis domain={[0, 5]} tickCount={6} tick={{ fontSize: 10 }} />
                            <Tooltip
                              formatter={(value) => [Number(value).toFixed(2), "Pontuação"]}
                              labelStyle={{ fontSize: 11 }}
                              contentStyle={{ fontSize: 11 }}
                            />
                            <Line
                              type="monotone"
                              dataKey="score"
                              stroke="#F97316"
                              strokeWidth={2.5}
                              dot={{ fill: "#F97316", r: 4 }}
                              activeDot={{ r: 6 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      <p className="text-[9px] text-gray-500 italic mt-1">
                        Eixo Y: pontuação de risco (0 = ambiente saudável; 5 = risco crítico).
                      </p>
                    </div>
                  )}

                  {/* Emissions table */}
                  <div className="text-[10px] uppercase tracking-wide text-gray-600 font-bold mb-1">
                    Histórico Detalhado
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border border-gray-300" data-testid="emissions-history-table">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border border-gray-300 px-2 py-1.5 text-left font-bold w-8">#</th>
                          <th className="border border-gray-300 px-2 py-1.5 text-left font-bold">Data</th>
                          <th className="border border-gray-300 px-2 py-1.5 text-left font-bold">Setor</th>
                          <th className="border border-gray-300 px-2 py-1.5 text-left font-bold">Função</th>
                          <th className="border border-gray-300 px-2 py-1.5 text-center font-bold w-20">Respostas</th>
                          <th className="border border-gray-300 px-2 py-1.5 text-center font-bold w-20">Pontuação</th>
                          <th className="border border-gray-300 px-2 py-1.5 text-left font-bold">Emitido por</th>
                          <th className="border border-gray-300 px-2 py-1.5 text-center font-bold w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {emissionHistory.map((em, idx) => {
                          const dateObj = new Date(em.created_at);
                          const riskInfo = getRiskLevel(em.overall_score || 0);
                          return (
                            <tr key={em.id} className={idx % 2 === 1 ? "bg-gray-50" : ""} data-testid={`emission-row-${idx}`}>
                              <td className="border border-gray-300 px-2 py-1.5 font-bold text-gray-500">
                                {emissionHistory.length - idx}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5">
                                <div className="font-semibold text-gray-900">{dateObj.toLocaleDateString("pt-BR")}</div>
                                <div className="text-[9px] text-gray-500">{dateObj.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-gray-700">
                                {em.setor || <span className="text-gray-400 italic">Multisetorial</span>}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-gray-700">
                                {em.funcao || <span className="text-gray-400 italic">Todas</span>}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-center font-semibold">
                                {em.total_assessments || 0}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-center">
                                <span className="font-bold" style={{ color: riskInfo.color }}>
                                  {Number(em.overall_score).toFixed(2)}
                                </span>
                                <span
                                  className="ml-1 px-1 py-0.5 rounded text-white text-[8px] font-bold uppercase"
                                  style={{ backgroundColor: riskInfo.color }}
                                >
                                  {riskInfo.level}
                                </span>
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-gray-700 text-[10px]">
                                {em.issued_by || "—"}
                              </td>
                              <td className="border border-gray-300 px-2 py-1.5 text-center">
                                <button
                                  data-testid={`emission-delete-${idx}`}
                                  onClick={async () => {
                                    if (!window.confirm("Remover este registro do histórico?")) return;
                                    try {
                                      const token = localStorage.getItem("admin_token");
                                      await axios.delete(`${API}/report-emissions/${em.id}`, {
                                        headers: { Authorization: `Bearer ${token}` },
                                      });
                                      fetchEmissionHistory();
                                    } catch (err) {
                                      console.error("Erro ao remover:", err);
                                    }
                                  }}
                                  className="text-red-500 hover:text-red-700"
                                  title="Remover registro"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* === Versão Interativa (QR Code) — print-only, appears at the end of the report ONLY for Multisetorial (empresa selecionada SEM setor) === */}
        {filters.empresa && !filters.setor && (() => {
          let presentationUrl;
          let isPublic = false;
          if (activeShareToken) {
            presentationUrl = buildPublicUrl(activeShareToken);
            isPublic = true;
          } else {
            const params = new URLSearchParams({ empresa: filters.empresa });
            if (filters.setor) params.append("setor", filters.setor);
            if (filters.funcao) params.append("funcao", filters.funcao);
            presentationUrl = `${window.location.origin}/presentation?${params.toString()}`;
          }
          return (
            <div
              className="hidden print:block report-interactive-page"
              data-testid="report-interactive-qr-page"
              data-toc-section="14"
            >
              <div className="flex flex-col items-center justify-center text-center px-8 py-12 min-h-[260mm]">
                <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-6 border border-orange-200">
                  <Smartphone className="w-3 h-3" />
                  Versão Interativa
                  {isPublic && <span className="text-green-700">· LINK PÚBLICO</span>}
                </div>

                <h2 className="text-3xl font-bold text-gray-900 mb-3">
                  Apresentação Executiva Online
                </h2>
                <p className="text-sm text-gray-600 max-w-xl leading-relaxed mb-8">
                  Escaneie o QR Code abaixo com a câmera do seu celular ou tablet para
                  acessar a versão interativa deste relatório. Ideal para reuniões com
                  a diretoria — apresenta os principais resultados em formato slideshow
                  com gráficos navegáveis.
                  {isPublic && (
                    <span className="block mt-2 text-green-700 font-semibold">
                      Este link é público e expira em{" "}
                      {new Date(activeShareToken.expires_at).toLocaleDateString("pt-BR")}.
                      Não requer login.
                    </span>
                  )}
                </p>

                {/* QR Code in a framed card */}
                <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 shadow-sm inline-block mb-6">
                  <QRCodeSVG
                    value={presentationUrl}
                    size={220}
                    level="M"
                    includeMargin={false}
                    bgColor="#ffffff"
                    fgColor="#1f2937"
                    data-testid="report-qrcode-svg"
                  />
                </div>

                {/* URL textual fallback */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 max-w-2xl mb-6">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-0.5">
                    Ou acesse pelo navegador
                  </p>
                  <p className="text-xs text-gray-700 font-mono break-all">{presentationUrl}</p>
                </div>

                {/* Instructions / what user will see */}
                <div className="grid grid-cols-3 gap-4 max-w-3xl w-full mt-4">
                  <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                    <div className="text-[10px] uppercase tracking-wide text-orange-700 font-bold mb-1">
                      1. Escaneie
                    </div>
                    <p className="text-[10px] text-gray-600 leading-snug">
                      Abra a câmera do celular ou app leitor de QR Code.
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                    <div className="text-[10px] uppercase tracking-wide text-orange-700 font-bold mb-1">
                      2. {isPublic ? "Visualize" : "Faça login"}
                    </div>
                    <p className="text-[10px] text-gray-600 leading-snug">
                      {isPublic
                        ? "A apresentação abre automaticamente — sem login."
                        : "Utilize as credenciais administrativas Viaseg para acessar."}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                    <div className="text-[10px] uppercase tracking-wide text-orange-700 font-bold mb-1">
                      3. Apresente
                    </div>
                    <p className="text-[10px] text-gray-600 leading-snug">
                      Navegue pelos slides interativos com setas, toque ou teclas.
                    </p>
                  </div>
                </div>

                <p className="text-[9px] uppercase tracking-[0.3em] text-gray-400 mt-10">
                  Viaseg · Assessoria em Medicina Ocupacional
                </p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Modal de Link Público (Compartilhar Apresentação) */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden" data-testid="share-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Link Público da Apresentação</h2>
                  <p className="text-xs text-gray-500">
                    {filters.empresa}
                    {filters.setor && <span> · {filters.setor}</span>}
                    {filters.funcao && <span> · {filters.funcao}</span>}
                  </p>
                </div>
              </div>
              <button onClick={closeShareModal} className="text-gray-400 hover:text-gray-600" data-testid="close-share-modal">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
                <p className="text-xs text-amber-900 leading-relaxed">
                  Quem possuir o link público poderá visualizar a apresentação <strong>sem fazer login</strong>.
                  Por segurança, dados sensíveis como pontuações numéricas detalhadas são automaticamente
                  ocultados — apenas níveis qualitativos (Baixo/Moderado/Alto/Crítico) são exibidos.
                </p>
              </div>

              {/* Create new */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 mb-5">
                <h3 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wide">Gerar novo link</h3>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Validade</label>
                    <select
                      value={shareDays}
                      onChange={(e) => setShareDays(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none text-sm"
                      data-testid="share-days-select"
                    >
                      <option value={30}>30 dias</option>
                      <option value={60}>60 dias</option>
                      <option value={90}>90 dias</option>
                    </select>
                  </div>
                  <button
                    onClick={handleCreateShareToken}
                    disabled={shareLoading}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold text-sm disabled:opacity-50"
                    data-testid="create-share-token-btn"
                  >
                    {shareLoading ? "Gerando..." : "Gerar Link"}
                  </button>
                </div>
              </div>

              {shareError && (
                <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700" data-testid="share-error">
                  {shareError}
                </div>
              )}

              {/* List active tokens */}
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                Links ativos ({shareTokens.length})
              </h3>
              {shareTokens.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-6 text-center border border-dashed border-gray-300">
                  <Smartphone className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Nenhum link público ativo para este escopo</p>
                </div>
              ) : (
                <ul className="space-y-2" data-testid="share-tokens-list">
                  {shareTokens.map((t) => {
                    const url = buildPublicUrl(t);
                    const exp = new Date(t.expires_at);
                    const daysLeft = Math.max(0, Math.ceil((exp - new Date()) / (1000 * 60 * 60 * 24)));
                    return (
                      <li
                        key={t.id}
                        className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                        data-testid={`share-token-${t.id}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded font-bold">ATIVO</span>
                            <span className="text-gray-600">{t.days} dias</span>
                            <span className="text-gray-400">·</span>
                            <span className="text-gray-500">
                              expira em {exp.toLocaleDateString("pt-BR")} ({daysLeft}d)
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleCopyShareLink(t.id, url)}
                              className="px-2 py-1 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded"
                              data-testid={`copy-share-${t.id}`}
                            >
                              {copiedTokenId === t.id ? "✓ Copiado" : "Copiar"}
                            </button>
                            <button
                              onClick={() => handleRevokeShareToken(t.id)}
                              className="px-2 py-1 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 rounded"
                              data-testid={`revoke-share-${t.id}`}
                            >
                              Revogar
                            </button>
                          </div>
                        </div>
                        <p className="text-[11px] font-mono text-gray-600 break-all bg-gray-50 px-2 py-1 rounded">
                          {url}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500">
                          {t.setor && <span>Setor: {t.setor}</span>}
                          {t.funcao && <span>· Função: {t.funcao}</span>}
                          {!t.setor && !t.funcao && <span>Escopo: Multisetorial (toda a empresa)</span>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={closeShareModal}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold text-sm"
                data-testid="close-share-modal-footer"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Modal de Histórico de Análises */}
      {showAiHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <History className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Histórico de Análises</h2>
                  <p className="text-sm text-gray-500">
                    {aiHistory.length} análise(s) salva(s)
                    {filters.empresa && <span className="text-indigo-600"> • {filters.empresa}</span>}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowAiHistory(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loadingHistory ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin mx-auto mb-4" />
                  <p className="text-gray-500">Carregando histórico...</p>
                </div>
              ) : aiHistory.length === 0 ? (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">
                    {filters.empresa 
                      ? `Nenhuma análise salva para ${filters.empresa}` 
                      : "Nenhuma análise salva"}
                  </p>
                  <p className="text-xs text-gray-400">Gere uma nova análise para salvar no histórico</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {aiHistory.map((analysis) => {
                    const meta = getModelMeta(analysis.ai_model);
                    const modelLabel = analysis.ai_model_label || meta.label;
                    return (
                    <div
                      key={analysis.id}
                      onClick={() => loadSavedAnalysis(analysis)}
                      className="bg-gray-50 hover:bg-indigo-50 rounded-xl p-4 border border-gray-200 hover:border-indigo-300 cursor-pointer transition-all group"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-bold text-gray-900 group-hover:text-indigo-700">{analysis.empresa}</h4>
                          <p className="text-sm text-gray-500">
                            {analysis.setor} • {analysis.funcao}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            data-testid="ai-history-model-badge"
                            className={`px-2 py-1 text-[10px] font-bold rounded border ${meta.badgeClass}`}
                            title={`Análise gerada com ${modelLabel}`}
                          >
                            <Sparkles className="w-3 h-3 inline mr-1 -mt-0.5" />
                            {modelLabel}
                          </span>
                          <span className={`px-2 py-1 text-xs font-bold rounded ${
                            analysis.overall_score <= 2.5 ? 'bg-green-100 text-green-700' :
                            analysis.overall_score <= 3.5 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {analysis.overall_score}/5
                          </span>
                          <button
                            onClick={(e) => deleteAnalysis(analysis.id, e)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                            title="Excluir análise"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center text-xs text-gray-400">
                        <span>{analysis.total_assessments} avaliações</span>
                        <span>{new Date(analysis.created_at).toLocaleString("pt-BR")}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">{analysis.interpretation.substring(0, 150)}...</p>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowAiHistory(false)}
                className="w-full py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all font-semibold"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Configurações */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Configurações</h2>
              <button
                onClick={() => {
                  setShowSettings(false);
                  setPasswordError("");
                  setPasswordSuccess("");
                  setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Senha Atual</label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nova Senha</label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Confirmar Nova Senha</label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                  required
                />
              </div>

              {passwordError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                  {passwordSuccess}
                </div>
              )}

              <button
                type="submit"
                className="w-full h-11 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all font-semibold"
              >
                Alterar Senha
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Print Styles - Optimized for A4 */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 10mm;
          }
          
          * {
            -webkit-print-color-adjust: exact !important; 
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          body { 
            font-size: 9pt !important;
          }
          
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          
          /* Compact spacing */
          .space-y-6 > * + * { margin-top: 8px !important; }
          .space-y-4 > * + * { margin-top: 6px !important; }
          .p-6, .p-8 { padding: 10px !important; }
          .p-5 { padding: 8px !important; }
          .mb-6, .mb-4 { margin-bottom: 6px !important; }
          .gap-6, .gap-8 { gap: 8px !important; }
          .gap-4 { gap: 6px !important; }
          
          /* Smaller text */
          h2 { font-size: 14pt !important; }
          h3 { font-size: 12pt !important; }
          h4 { font-size: 10pt !important; }
          .text-2xl { font-size: 14pt !important; }
          .text-xl { font-size: 12pt !important; }
          .text-lg { font-size: 11pt !important; }
          .text-sm { font-size: 8pt !important; }
          .text-xs { font-size: 7pt !important; }
          
          /* ============================================
             HIDE SVG Charts in Print - Use CSS alternatives
             ============================================ */
          
          /* Hide Recharts containers in print */
          .print\\:hidden {
            display: none !important;
          }
          
          /* Show CSS chart alternatives in print */
          .hidden.print\\:block {
            display: block !important;
          }
          
          /* Grid layout for dimension cards - 2 columns */
          .grid.grid-cols-1.lg\\:grid-cols-2 {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 8px !important;
          }
          
          /* Dimension cards */
          .bg-white.rounded-xl {
            break-inside: avoid; 
            page-break-inside: avoid;
          }
          
          /* Smaller dimension cards */
          .rounded-xl { border-radius: 6px !important; }
          
          /* AI Section compact */
          .bg-gradient-to-br { 
            background: #f5f3ff !important;
            break-inside: avoid;
          }
          
          /* Suggestion items compact */
          .bg-green-50, .bg-orange-50 {
            padding: 6px !important;
            margin-bottom: 4px !important;
          }
          
          /* Hide decorative elements */
          .shadow-sm, .shadow-lg, .shadow-xl { box-shadow: none !important; }
          
          /* Compact header info */
          .max-w-7xl { max-width: 100% !important; }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
